import {
    ICompileService,
    IFilterService,
    IRootScopeService,
    IScope,
    ITemplateLinkingFunction,
    ITimeoutService
} from "angular";
import {PlaceOrderRequest} from "../../models/Order";
import {OrderUtil} from "../../util/OrderUtil";
import {OrderRequestTemplate, SingleOrderFormatTemplate, TmcOrderFormatTemplate} from "./OrderFormatTemplates";
import {Optional} from "../../../js/util/Optional";
import {
    isOptionLegModel,
    isOptionOrderViewModel,
    isTmcOrderViewModel,
    OptionOrderViewModel,
    OrderViewModel,
    TmcOrderLegViewModel,
    TmcOrderOptionLegViewModel,
    TmcOrderViewModel
} from "../../models/OrderViewModel";
import {PriceStrategy} from "../../../components/inputs/price/input/PriceStrategy";

export class OrderFormatService {

    public static id = "orderFormatService";

    private readonly orderRequest: ITemplateLinkingFunction;
    private readonly singleOrder: ITemplateLinkingFunction;
    private readonly tmcOrder: ITemplateLinkingFunction;
    private readonly translateFilter: (value: string) => string;

    private container: JQuery;

    /** @ngInject */
    public constructor(private readonly $rootScope: IRootScopeService,
                       $compile: ICompileService,
                       $filter: IFilterService,
                       private readonly $timeout: ITimeoutService) {
        this.orderRequest = $compile(OrderRequestTemplate);
        this.singleOrder = $compile(SingleOrderFormatTemplate);
        this.tmcOrder = $compile(TmcOrderFormatTemplate);
        this.translateFilter = $filter("translate");
        this.init();
    }

    private init() {
        this.container = $("<div></div>");
        this.container.css("display", "none");
        $(document.body).append(this.container);
    }

    public format(order: PlaceOrderRequest | OrderViewModel): Promise<string> {
        if (this.isOrderViewModel(order)) {
            return this.formatOrder(order);
        } else {
            return this.formatOrderRequest(order);
        }
    }

    private isOrderViewModel(order: any): order is OrderViewModel {
        return "quote" in order;
    }

    private formatOrderRequest(order: PlaceOrderRequest): Promise<string> {
        const props = this.orderRequestProps(order);
        const scope = this.scope(props);
        return this.compile(this.orderRequest, scope);
    }

    private orderRequestProps(order: PlaceOrderRequest): any {
        const side = OrderUtil.side(order).substr(0, 1).toUpperCase();
        return {
            side,
            quantity: order.quantity,
            code: order.code,
            limit: order.limit
        };
    }

    private formatOrder(model: OrderViewModel): Promise<string> {
        if (isTmcOrderViewModel(model)) {
            return this.formatTmc(model);
        } else {
            return this.formatSingle(model);
        }
    }

    private formatSingle(model: OrderViewModel): Promise<string> {
        const baseProps = this.orderProps(model);
        const orderProps = this.singleOrderProps(model);
        const scope = this.scope(baseProps, orderProps);
        return this.compile(this.singleOrder, scope);
    }

    private orderProps(order: OrderViewModel): any {
        // A really lazy workaround for handling both created and completed orders
        const quantity = (order.filledQuantity === 0) ? order.quantity : order.filledQuantity;
        const limit = (order.averagePrice === 0) ? order.limit : order.averagePrice;
        return {
            accountNumber: order.account?.number,
            side: order.side,
            quantity,
            code: order.code,
            limit: limit?.toFixed(6)
        };
    }

    private singleOrderProps(model: OrderViewModel): any {
        if (isOptionOrderViewModel(model)) {
            return this.optionOrderProps(model, model.priceStrategy);
        } else {
            return {isOption: false};
        }
    }

    private optionOrderProps(order: OptionOrderViewModel | TmcOrderOptionLegViewModel,
                             priceStrategy: PriceStrategy): any {
        const strike = Optional.of(order)
                               .map((o) => o.strike)
                               .map((s) => priceStrategy.format(s))
                               .orElse("");
        return {
            strike,
            style: order.style,
            type: order.type,
            expiry: order.expiry,
            isOption: true
        };
    }

    private formatTmc(model: TmcOrderViewModel): Promise<string> {
        const baseProps = this.orderProps(model);
        const tmcProps = this.tmcOrderProps(model);
        const scope = this.scope(baseProps, tmcProps);
        return this.compile(this.tmcOrder, scope);
    }

    private tmcOrderProps(model: TmcOrderViewModel): any {

        const code = model.code;
        const underlying = model.code.substr(0, 3);
        const quantity = model.quantity;
        const limit = model.limit;
        const legs = model.legs.map((leg) => this.legProps(leg, model.priceStrategy));
        return {
            code,
            underlying,
            quantity,
            limit,
            legs
        };
    }

    private legProps(leg: TmcOrderLegViewModel, priceStrategy: PriceStrategy): any {
        const side = leg.side;
        const ratio = leg.ratio;
        const code = leg.code;
        const optionProps = Optional.of(leg)
                                    .filter(isOptionLegModel)
                                    .map((l) => this.optionOrderProps(l, priceStrategy))
                                    .orElse({});
        const baseProps = {
            side, ratio, code
        };
        return Object.assign({}, baseProps, optionProps);
    }

    private scope(...props: Array<any>): IScope {
        const scope = this.$rootScope.$new();
        return Object.assign(scope, ...props);
    }

    private compile(linker: ITemplateLinkingFunction, scope: IScope): Promise<string> {
        return new Promise<string>((resolve) => {
            linker(scope, (node) => {
                // We need to append the node so that angular runs its bindings and then clean up after
                scope.$applyAsync(() => {
                    this.container.append(node!);
                    this.$timeout(() => {
                        resolve(node!.html());
                        node!.remove();
                        scope.$destroy();
                    });
                });
            });
        });
    }
}
